import { motion } from 'framer-motion';
import { ScanBarcode, Clock, ShoppingBag, Shield, Smartphone, Receipt, BarChart3, Store } from 'lucide-react';

const features = [
  {
    icon: ScanBarcode,
    title: 'Instant Barcode Scanning',
    description: 'Point your phone at any product barcode. Our AI-powered scanner reads it in milliseconds and adds it to your cart.',
    color: 'bg-brand-primary/10',
    iconColor: 'text-brand-primary',
  },
  {
    icon: Clock,
    title: 'Zero Wait Time',
    description: 'Skip the checkout queue entirely. Pay directly from your phone and walk out. Shopping just got 10x faster.',
    color: 'bg-functional-success/10',
    iconColor: 'text-functional-success',
  },
  {
    icon: ShoppingBag,
    title: 'Smart Cart Management',
    description: 'Track your spending in real-time. Add or remove items easily. Get a complete overview before you pay.',
    color: 'bg-brand-accent/10',
    iconColor: 'text-brand-accent',
  },
  {
    icon: Shield,
    title: 'Secure Payments',
    description: 'Bank-level encryption protects every transaction. PCI DSS compliant with fraud detection built-in.',
    color: 'bg-functional-warning/10',
    iconColor: 'text-functional-warning',
  },
  {
    icon: Smartphone,
    title: 'Works on Any Phone',
    description: 'iOS or Android, new or old. If it has a camera, it works with GrabPe. No special hardware needed.',
    color: 'bg-brand-primary/10',
    iconColor: 'text-brand-primary',
  },
  {
    icon: Receipt,
    title: 'Digital Receipts',
    description: 'No more paper clutter. Get instant digital receipts in the app. Easy returns and expense tracking.',
    color: 'bg-brand-accent/10',
    iconColor: 'text-brand-accent',
  },
  {
    icon: BarChart3,
    title: 'Store Analytics',
    description: 'Retailers get real-time insights on shopping patterns, peak hours, and popular products.',
    color: 'bg-functional-success/10',
    iconColor: 'text-functional-success',
  },
  {
    icon: Store,
    title: 'Easy Store Integration',
    description: 'Connect your existing POS and inventory system. Go live in days, not months. Minimal setup required.',
    color: 'bg-functional-warning/10',
    iconColor: 'text-functional-warning',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0 },
};

const Features = () => {
  return (
    <section id="features" className="py-20 md:py-32 bg-neutral-bg-secondary">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-sm font-semibold tracking-wide uppercase text-brand-primary mb-4 block">
            Features
          </span>
          <h2 className="font-heading text-4xl md:text-5xl font-bold tracking-tight text-brand-secondary mb-6">
            Why shoppers &amp; stores{' '}
            <span className="bg-clip-text text-transparent bg-gradient-to-r from-brand-primary to-brand-accent">
              love GrabPe
            </span>
          </h2>
          <p className="text-lg md:text-xl font-medium leading-relaxed text-neutral-text-secondary max-w-2xl mx-auto">
            A better shopping experience for customers. More efficiency and insights for retailers.
          </p>
        </motion.div>

        {/* Features Grid - Bento Style */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
        >
          {features.map((feature, index) => (
            <motion.div
              key={feature.title}
              variants={itemVariants}
              className={`bg-white rounded-2xl p-8 border border-slate-100 shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_8px_24px_rgba(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-1 group ${
                index === 0 || index === 5 ? 'md:col-span-2 lg:col-span-2' : ''
              }`}
              data-testid={`feature-card-${index}`}
            >
              <div className={`w-14 h-14 ${feature.color} rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300`}>
                <feature.icon className={`w-7 h-7 ${feature.iconColor}`} />
              </div>
              <h3 className="font-heading text-xl font-bold text-brand-secondary mb-3">
                {feature.title}
              </h3>
              <p className="text-neutral-text-secondary leading-relaxed">
                {feature.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Stats Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-20 grid grid-cols-2 md:grid-cols-4 gap-8"
        >
          {[
            { value: '500K+', label: 'Happy Shoppers' },
            { value: '2,500+', label: 'Partner Stores' },
            { value: '12 min', label: 'Avg. Time Saved' },
            { value: '4.9', label: 'App Rating' },
          ].map((stat) => (
            <div key={stat.label} className="text-center" data-testid={`stat-${stat.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <p className="font-heading text-4xl md:text-5xl font-black text-brand-secondary mb-2">
                {stat.value}
              </p>
              <p className="text-neutral-text-secondary font-medium">{stat.label}</p>
            </div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
